// Iter-194x — BAIM Help Chatbot
//
// Floating bottom-right button. When clicked, slides up a chat panel with:
//   • Message history (persisted in sessionStorage so a refresh keeps the
//     conversation alive; tab close & explicit X/logout drop it).
//   • Auto-scroll on new message
//   • Quick suggestions to seed the conversation
//   • Backend: POST /api/chatbot/message with the running history
//
// Iter-195 — Added a "minimize" affordance (chevron) which collapses the
// panel back into the floating launcher *without* erasing history. The X
// button now explicitly clears the history. The launcher displays a small
// blue dot when minimized with unread/preserved messages.
//
// Iter-195d — Polished UX:
//   • Launcher uses the lucide `Bot` icon (clearly identifies the assistant
//     vs. a generic chat bubble) and gets a subtle floating animation +
//     attention pulse halo so users notice it.
//   • Hover tooltip on the launcher tells the user what the bot can do
//     ("Workflows, raccourcis, débogage…").
//   • The empty-state shows a welcoming assistant-style speech bubble in
//     addition to the suggestion chips so the first impression matches an
//     actual conversation.
//
// Available to ALL authenticated profiles.
import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Bot, X, Send, Loader2, Sparkles, Minus } from "lucide-react";

const SUGGESTIONS = [
  "Comment je saisis mon CRA ?",
  "Comment soumettre une note de frais ?",
  "Où trouver les relances factures ?",
  "Comment clôturer une mission ?",
];

// Iter-195k — Rotating "thinking" messages displayed while we wait for the
// LLM. Captivating + reassuring so the wait feels like a thoughtful pause
// rather than a stall. Rotated every ~1.4s.
// Iter-195l — Contextual messages: each set is keyword-matched against the
// user's question so the bot feels like it already understands the intent.
// Falls back to a generic set if no keyword matches.
const THINKING_BY_CONTEXT = {
  cra: [
    "Je consulte les règles de saisie des CRA…",
    "Je vérifie la procédure de validation…",
    "J'examine les statuts (Draft / Submitted / Approved)…",
    "J'assemble les bonnes étapes pour vous…",
  ],
  facture: [
    "Je consulte les workflows de facturation…",
    "Je vérifie les délais et les relances…",
    "J'examine les modèles de relance milestone vs overdue…",
    "Je finalise une réponse claire…",
  ],
  ndf: [
    "Je consulte les règles des notes de frais…",
    "Je vérifie le circuit de validation…",
    "J'examine les justificatifs et l'OCR…",
    "J'assemble la procédure complète…",
  ],
  mission: [
    "Je consulte le cycle de vie des missions…",
    "Je vérifie les étapes de clôture et de facturation…",
    "J'examine les rôles d'équipe et le staffing…",
    "Je finalise la réponse…",
  ],
  simulation: [
    "Je consulte le simulateur de mission…",
    "Je vérifie la génération de devis et l'archivage…",
    "J'examine la formule de marge et la TVA…",
    "Je finalise la réponse…",
  ],
  client: [
    "Je consulte la fiche client et les contrats…",
    "Je vérifie les délais de paiement spécifiques…",
    "J'examine la hiérarchie client → mission…",
    "Je finalise la réponse…",
  ],
  conge: [
    "Je consulte les règles de congés / RTT…",
    "Je vérifie le solde et les approbations…",
    "J'examine le calendrier d'équipe…",
    "Je finalise la réponse…",
  ],
  generic: [
    "Je consulte la documentation BAIM…",
    "Je cherche la meilleure réponse pour vous…",
    "Je vérifie les workflows et les rôles…",
    "Je formule une réponse claire et concise…",
    "Encore un instant, je finalise…",
    "J'assemble les bonnes étapes…",
  ],
};

// Iter-195l — Pick the message set whose keyword pattern best matches the
// question. Returns the keyed array (e.g. THINKING_BY_CONTEXT.cra). If
// nothing matches, returns the generic set.
function pickThinkingSet(question) {
  const q = (question || "").toLowerCase();
  // Order matters: more specific intents first.
  if (/(facture|invoice|relance|impayé|paiement|encaiss)/.test(q)) return THINKING_BY_CONTEXT.facture;
  if (/(simulation|devis|simulateur|marge|kpi mission)/.test(q)) return THINKING_BY_CONTEXT.simulation;
  if (/(\bcra\b|timesheet|saisie|jour travaillé|jour saisi)/.test(q)) return THINKING_BY_CONTEXT.cra;
  if (/(note de frais|\bndf\b|ticket|justificatif|expense|ocr)/.test(q)) return THINKING_BY_CONTEXT.ndf;
  if (/(congé|conge|rtt|leave|absence|jour férié|jour ferie)/.test(q)) return THINKING_BY_CONTEXT.conge;
  if (/(client|contrat|prospect|deal|onboard)/.test(q)) return THINKING_BY_CONTEXT.client;
  if (/(mission|staff|équipe|equipe|clôture|cloture|delivery)/.test(q)) return THINKING_BY_CONTEXT.mission;
  return THINKING_BY_CONTEXT.generic;
}

// sessionStorage keys — auto-cleared on tab close. We also wipe them
// manually on the X button and on logout.
const SS_KEY_MESSAGES = "baim_chatbot_messages_v1";
const SS_KEY_SESSION = "baim_chatbot_session_v1";

function _loadSession() {
  try {
    const raw = sessionStorage.getItem(SS_KEY_MESSAGES);
    const sid = sessionStorage.getItem(SS_KEY_SESSION);
    const messages = raw ? JSON.parse(raw) : [];
    return { messages: Array.isArray(messages) ? messages : [], sessionId: sid || null };
  } catch {
    return { messages: [], sessionId: null };
  }
}

function _saveMessages(messages) {
  try {
    if (!messages || messages.length === 0) {
      sessionStorage.removeItem(SS_KEY_MESSAGES);
    } else {
      sessionStorage.setItem(SS_KEY_MESSAGES, JSON.stringify(messages));
    }
  } catch { /* quota errors ignored */ }
}

function _saveSession(sid) {
  try { if (sid) sessionStorage.setItem(SS_KEY_SESSION, sid); } catch {}
}

function _clearSession() {
  try {
    sessionStorage.removeItem(SS_KEY_MESSAGES);
    sessionStorage.removeItem(SS_KEY_SESSION);
  } catch {}
}

// Exported helper so AppLayout / Logout can wipe the chat history on logout.
export function clearChatBotHistory() { _clearSession(); }

// Iter-195i — Markdown components map: tailwind-styled overrides so Claude's
// **bold**, lists, headings, inline code, and links render legibly inside
// the chat bubble without breaking the layout. We keep things tight (small
// margins) to match the compact widget footprint.
const MD_COMPONENTS = {
  p: ({ node, ...props }) => <p className="mb-1.5 last:mb-0 leading-relaxed" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-4 mb-1.5 last:mb-0 space-y-0.5" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-4 mb-1.5 last:mb-0 space-y-0.5" {...props} />,
  li: ({ node, ...props }) => <li className="leading-snug" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-zinc-950" {...props} />,
  em: ({ node, ...props }) => <em className="italic text-zinc-700" {...props} />,
  h1: ({ node, ...props }) => <h3 className="text-sm font-bold mt-2 mb-1 first:mt-0" {...props} />,
  h2: ({ node, ...props }) => <h3 className="text-sm font-bold mt-2 mb-1 first:mt-0" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-xs font-bold uppercase tracking-wider mt-2 mb-1 first:mt-0 text-zinc-700" {...props} />,
  code: ({ node, inline, ...props }) =>
    inline ? (
      <code className="px-1 py-0.5 rounded bg-zinc-100 text-zinc-800 font-mono text-[11px] border border-zinc-200" {...props} />
    ) : (
      <code className="block p-2 rounded bg-zinc-900 text-zinc-100 font-mono text-[11px] overflow-x-auto" {...props} />
    ),
  a: ({ node, ...props }) => (
    <a className="text-blue-600 hover:underline" target="_blank" rel="noreferrer noopener" {...props} />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-2 border-zinc-300 pl-2 text-zinc-600 my-1" {...props} />
  ),
  hr: () => <hr className="my-2 border-zinc-200" />,
};

function AssistantMarkdown({ text, withCaret = false }) {
  return (
    <div className="text-sm">
      <ReactMarkdown components={MD_COMPONENTS}>
        {text}
      </ReactMarkdown>
      {withCaret && (
        <span
          aria-hidden
          className="inline-block w-[2px] h-3.5 bg-zinc-700 ml-0.5 align-middle animate-pulse"
        />
      )}
    </div>
  );
}

export default function ChatBotWidget() {
  const { user } = useAuth();
  // 3 states: closed (launcher visible), open (panel visible), minimized
  // (launcher visible, history preserved).
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);   // [{role, content}]
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  // Iter-195d — show the hover tooltip once the user pauses over the launcher.
  // Iter-195l — On touch devices the tooltip won't fire onMouseLeave so we
  // auto-hide it after 3.5s + dismiss it on any document tap outside the
  // launcher area. Also, calling setTooltipVisible(false) on launcher click
  // ensures it can't linger when the panel opens.
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const launcherWrapRef = useRef(null);
  const tooltipTimerRef = useRef(null);

  // Iter-195l — auto-hide tooltip after 3.5s (covers touch devices that
  // never fire mouseleave).
  useEffect(() => {
    if (!tooltipVisible) return;
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    tooltipTimerRef.current = setTimeout(() => setTooltipVisible(false), 3500);
    return () => {
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    };
  }, [tooltipVisible]);

  // Iter-195l — dismiss tooltip on any pointerdown outside the launcher.
  useEffect(() => {
    if (!tooltipVisible) return;
    const onPointer = (e) => {
      if (!launcherWrapRef.current) return;
      if (!launcherWrapRef.current.contains(e.target)) {
        setTooltipVisible(false);
      }
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [tooltipVisible]);
  // Iter-195h — typewriter streaming effect: hold the in-flight reply here
  // and reveal it character-by-character so the user perceives ChatGPT-style
  // progressive text. Once fully revealed, we commit it to `messages`.
  const [streaming, setStreaming] = useState(null); // { fullText, revealed }
  const streamTimerRef = useRef(null);
  // Iter-195k/l — rotating reassuring "thinking" message during the busy state.
  // The set is picked based on keywords in the user's question (or generic).
  const [thinkingIdx, setThinkingIdx] = useState(0);
  const [thinkingSet, setThinkingSet] = useState(THINKING_BY_CONTEXT.generic);
  const scrollRef = useRef(null);

  // Iter-195k — Rotate the thinking message every 1.4s while busy.
  useEffect(() => {
    if (!busy) {
      setThinkingIdx(0);
      return;
    }
    const t = setInterval(() => {
      setThinkingIdx((i) => (i + 1) % thinkingSet.length);
    }, 1400);
    return () => clearInterval(t);
  }, [busy, thinkingSet]);

  // On mount: hydrate from sessionStorage so refreshes keep the conversation.
  useEffect(() => {
    const { messages: stored, sessionId: storedSid } = _loadSession();
    if (stored.length) setMessages(stored);
    if (storedSid) setSessionId(storedSid);
  }, []);

  // Persist messages on every change.
  useEffect(() => { _saveMessages(messages); }, [messages]);
  useEffect(() => { if (sessionId) _saveSession(sessionId); }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy, open, streaming]);

  // Iter-195h — cleanup the typewriter timer on unmount.
  useEffect(() => () => {
    if (streamTimerRef.current) clearInterval(streamTimerRef.current);
  }, []);

  // Iter-195j — Pre-warm the LLM connection as soon as the chatbot panel
  // opens. This runs in parallel with the user typing their question, so by
  // the time they hit "Send" the upstream is already hot and the first real
  // call returns in ~1s instead of timing out. Failure is silent — the
  // warmup is best-effort, the message path has its own retry.
  const warmedRef = useRef(false);
  useEffect(() => {
    if (!open || warmedRef.current) return;
    warmedRef.current = true;
    api.post("/chatbot/warmup", {}).catch(() => { /* silent */ });
  }, [open]);

  const minimize = useCallback(() => setOpen(false), []);
  const closeAndClear = useCallback(() => {
    if (streamTimerRef.current) {
      clearInterval(streamTimerRef.current);
      streamTimerRef.current = null;
    }
    setStreaming(null);
    setOpen(false);
    setMessages([]);
    setSessionId(null);
    setInput("");
    _clearSession();
  }, []);

  // Iter-195 — on every messages change, persist BUT also remove the key
  // entirely when the array is empty (instead of writing '[]') so logout
  // and "X close" leave a consistent null state in sessionStorage.

  if (!user) return null;

  // Iter-195h — start the typewriter reveal for an assistant reply.
  // Reveals ~4 characters every 14ms (~280 chars/sec) which feels natural
  // for French prose without being slower than reading speed. Once fully
  // revealed, the message is committed to the `messages` array.
  const startTypewriter = (fullText, baseMessages) => {
    if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    setStreaming({ fullText, revealed: "" });
    let idx = 0;
    const step = 4;
    streamTimerRef.current = setInterval(() => {
      idx = Math.min(fullText.length, idx + step);
      setStreaming({ fullText, revealed: fullText.slice(0, idx) });
      if (idx >= fullText.length) {
        clearInterval(streamTimerRef.current);
        streamTimerRef.current = null;
        // Commit & clear the streaming buffer in the same tick to avoid a flicker.
        setMessages([...baseMessages, { role: "assistant", content: fullText }]);
        setStreaming(null);
      }
    }, 14);
  };

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t) return;
    const next = [...messages, { role: "user", content: t }];
    setMessages(next);
    setInput("");
    // Iter-195l — pick the contextual thinking message set BEFORE setBusy so
    // the very first rotation is already on-topic.
    setThinkingSet(pickThinkingSet(t));
    setThinkingIdx(0);
    setBusy(true);
    try {
      const r = await api.post("/chatbot/message", {
        message: t,
        history: next.slice(0, -1),  // backend appends the new turn itself
        session_id: sessionId,
      });
      const reply = r.data?.reply || "(pas de réponse)";
      if (r.data?.session_id && !sessionId) setSessionId(r.data.session_id);
      // Iter-195h — drop the typing indicator and start the typewriter reveal.
      setBusy(false);
      startTypewriter(reply, next);
      return;
    } catch (e) {
      // Iter-195 — friendlier error messages so the user knows what to do.
      let msg;
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        msg = "Le service Assistant n'est pas encore déployé sur cet environnement. Réessayez après le prochain déploiement, ou contactez l'équipe technique.";
      } else if (status === 502 || status === 503 || status === 504) {
        msg = "Le service Assistant est momentanément indisponible. Cliquez de nouveau sur votre question — l'appel précédent a sans doute été un timeout ponctuel.";
      } else if (status === 401 || status === 403) {
        msg = "Votre session a expiré. Reconnectez-vous au portail.";
      } else if (typeof detail === "string" && detail.trim()) {
        msg = detail;
      } else {
        msg = "Erreur de communication avec l'Assistant. Réessayez ou contactez le support.";
      }
      setMessages([...next, { role: "assistant", content: `⚠️ ${msg}` }]);
    } finally { setBusy(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const hasHistory = messages.length > 0;

  return (
    <>
      {/* Iter-195d/e — Floating launcher: lucide Bot icon, gentle floating
          animation (CSS @keyframes baim-bot-float), attention-pulse halo,
          hover tooltip explaining what the bot can do. The little blue dot
          still signals preserved conversation.
          Iter-195e — on hover the bot "evolves": the Bot icon scales+spins,
          a Sparkles overlay fades in and rotates the opposite way, three
          orbiting Stars dots burst out, and a gradient ring lights up. The
          whole thing reads as a small transformation sequence.
          Iter-195f — on mobile the launcher sits ABOVE the BottomNav
          (bottom-20 ≈ 80px) so it never overlaps the tab bar. Desktop keeps
          bottom-5. */}
      {!open && (
        <div className="fixed bottom-20 md:bottom-5 right-5 z-50" ref={launcherWrapRef}>
          {/* Pulse halo — attention grabber that fades on hover so it never
              competes with the tooltip. */}
          <span
            aria-hidden
            className="absolute inset-0 rounded-full bg-zinc-950 opacity-40 animate-ping pointer-events-none"
            style={{ animationDuration: "2.4s" }}
          />
          <button
            onClick={() => { setTooltipVisible(false); setOpen(true); }}
            onMouseEnter={() => setTooltipVisible(true)}
            onMouseLeave={() => setTooltipVisible(false)}
            onFocus={() => setTooltipVisible(true)}
            onBlur={() => setTooltipVisible(false)}
            data-testid="chatbot-launcher"
            aria-label="Open BAIM Assistant"
            className="group relative h-12 w-12 rounded-full bg-zinc-950 text-white shadow-lg hover:bg-gradient-to-br hover:from-indigo-600 hover:via-zinc-900 hover:to-fuchsia-600 hover:shadow-[0_0_24px_rgba(168,85,247,0.55)] flex items-center justify-center transition-all duration-500 hover:scale-110"
            style={{ animation: "baim-bot-float 3.4s ease-in-out infinite" }}
          >
            {/* Outer gradient ring that lights up on hover */}
            <span
              aria-hidden
              className="absolute -inset-1 rounded-full bg-gradient-to-tr from-indigo-500 via-fuchsia-500 to-amber-300 opacity-0 group-hover:opacity-70 blur-md transition-opacity duration-500 pointer-events-none"
            />
            {/* Bot icon: scales + rotates 360° over the hover transition */}
            <Bot
              size={22}
              strokeWidth={2.2}
              className="relative z-10 transition-transform duration-700 ease-out group-hover:rotate-[360deg] group-hover:scale-110"
            />
            {/* Sparkles overlay — appears on hover, rotates opposite direction
                so the icon feels like it's "transforming". */}
            <Sparkles
              size={22}
              strokeWidth={2.2}
              aria-hidden
              className="absolute z-10 text-amber-300 opacity-0 group-hover:opacity-100 transition-all duration-700 ease-out scale-50 group-hover:scale-100 group-hover:rotate-[-180deg] mix-blend-screen"
            />
            {/* Three orbiting "evolution" particles that burst on hover. */}
            <span aria-hidden className="pointer-events-none absolute inset-0">
              <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 h-1.5 w-1.5 rounded-full bg-amber-300 opacity-0 group-hover:opacity-100 group-hover:-translate-y-4 transition-all duration-500 delay-100" />
              <span className="absolute bottom-0 left-1 h-1.5 w-1.5 rounded-full bg-fuchsia-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-[-12px] group-hover:translate-y-2 transition-all duration-500 delay-150" />
              <span className="absolute bottom-0 right-1 h-1.5 w-1.5 rounded-full bg-indigo-300 opacity-0 group-hover:opacity-100 group-hover:translate-x-3 group-hover:translate-y-2 transition-all duration-500 delay-200" />
            </span>
            {hasHistory && (
              <span
                data-testid="chatbot-launcher-dot"
                className="absolute z-20 -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-blue-500 border-2 border-white"
                title="Conversation en cours"
              />
            )}
          </button>
          {/* Hover tooltip — describes what the assistant can do for the user. */}
          <div
            data-testid="chatbot-launcher-tooltip"
            role="tooltip"
            className={`absolute bottom-full right-0 mb-3 w-64 origin-bottom-right transition-all duration-200 ${
              tooltipVisible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-1 pointer-events-none"
            }`}
          >
            <div className="bg-zinc-950 text-white rounded-lg shadow-xl px-3 py-2.5 text-xs leading-relaxed">
              <div className="flex items-center gap-1.5 font-semibold mb-1">
                <Sparkles size={12} /> BAIM Assistant
              </div>
              <p className="opacity-90">
                Posez-moi des questions sur les workflows, raccourcis, validations CRA, relances factures, ou la clôture d'une mission — je vous oriente directement vers la bonne action.
              </p>
              <div className="text-[10px] opacity-50 mt-1.5">Cliquez pour démarrer la conversation</div>
              {/* Tooltip arrow */}
              <span
                aria-hidden
                className="absolute -bottom-1.5 right-4 h-3 w-3 bg-zinc-950 rotate-45"
              />
            </div>
          </div>
        </div>
      )}

      {/* Panel + tap-outside backdrop.
          Iter-195f — Clicking the backdrop minimizes the panel (preserves
          history). On mobile we also raise the panel above the BottomNav. */}
      {open && (
        <>
          <div
            data-testid="chatbot-backdrop"
            onClick={minimize}
            className="fixed inset-0 z-40 bg-transparent md:bg-transparent"
            aria-hidden
          />
          <div
            data-testid="chatbot-panel"
            className="fixed bottom-20 md:bottom-5 right-5 z-50 w-[400px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-7rem)] md:max-h-[calc(100vh-4rem)] bg-white border border-zinc-200 rounded-xl shadow-2xl flex flex-col"
          >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-gradient-to-r from-zinc-950 to-zinc-800 text-white rounded-t-xl">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Bot size={18} strokeWidth={2.2} />
                <Sparkles size={9} className="absolute -top-1 -right-1 text-amber-300" />
              </div>
              <div>
                <div className="text-sm font-semibold">BAIM Assistant</div>
                <div className="text-[10px] opacity-70">Powered by Claude Haiku · {user.role}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={minimize}
                data-testid="chatbot-minimize"
                aria-label="Minimize"
                title="Réduire (conserve l'historique)"
                className="p-1.5 hover:bg-white/10 rounded-md"
              >
                <Minus size={16} />
              </button>
              <button
                onClick={closeAndClear}
                data-testid="chatbot-close"
                aria-label="Close and clear history"
                title="Fermer et effacer l'historique"
                className="p-1.5 hover:bg-white/10 rounded-md"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chatbot-messages"
            className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-zinc-50/30"
          >
            {messages.length === 0 && (
              <div data-testid="chatbot-welcome">
                {/* Iter-195d — Welcome bubble styled like an assistant message
                    so the first impression matches a real conversation. */}
                <div className="flex justify-start mb-3 animate-in fade-in slide-in-from-left-2 duration-500">
                  <div className="max-w-[88%] bg-white border border-zinc-200 text-zinc-900 rounded-2xl rounded-tl-sm px-3.5 py-2.5 shadow-sm">
                    <div className="flex items-center gap-1.5 mb-1.5 text-zinc-500">
                      <Bot size={13} strokeWidth={2.2} />
                      <span className="text-[10px] uppercase tracking-widest font-mono">Assistant</span>
                    </div>
                    <div className="text-sm leading-relaxed">
                      <span className="font-semibold">Bonjour {user.name?.split(" ")[0] || ""}</span>
                      <br />
                      Je suis votre <strong>assistant BAIM</strong>. Je peux vous aider à :
                    </div>
                    <ul className="text-xs text-zinc-700 mt-2 space-y-0.5 list-none">
                      <li>• Naviguer dans les workflows (CRA, NDF, missions)</li>
                      <li>• Trouver une fonctionnalité ou un raccourci</li>
                      <li>• Comprendre les statuts, relances et validations</li>
                      <li>• Diagnostiquer un blocage métier</li>
                    </ul>
                    <div className="text-[11px] text-zinc-500 mt-2">
                      Posez votre question, ou choisissez une suggestion ci-dessous.
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`chatbot-suggestion-${i}`}
                      className="text-left px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chatbot-msg-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm break-words ${
                    m.role === "user"
                      ? "bg-zinc-950 text-white rounded-tr-sm whitespace-pre-wrap"
                      : "bg-white border border-zinc-200 text-zinc-900 rounded-tl-sm"
                  }`}
                >
                  {m.role === "assistant"
                    ? <AssistantMarkdown text={m.content} />
                    : m.content}
                </div>
              </div>
            ))}
            {/* Iter-195h/i — Streaming assistant reply: revealed markdown + blinking caret. */}
            {streaming && (
              <div className="flex justify-start" data-testid="chatbot-streaming">
                <div className="max-w-[85%] bg-white border border-zinc-200 text-zinc-900 rounded-2xl rounded-tl-sm px-3 py-2 text-sm break-words">
                  <AssistantMarkdown text={streaming.revealed} withCaret />
                </div>
              </div>
            )}
            {busy && (
              <div className="flex justify-start" data-testid="chatbot-thinking">
                <div className="bg-white border border-zinc-200 px-3 py-2 rounded-2xl rounded-tl-sm flex items-center gap-2 min-w-[200px]">
                  {/* Three bouncing dots = "typing" */}
                  <span className="flex items-center gap-1" aria-hidden>
                    <span className="h-1.5 w-1.5 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                  <span
                    key={thinkingIdx}
                    data-testid="chatbot-thinking-text"
                    className="text-xs text-zinc-600 italic animate-in fade-in slide-in-from-bottom-1 duration-300"
                  >
                    {thinkingSet[thinkingIdx]}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-zinc-100 p-2 bg-white rounded-b-xl">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Posez votre question…"
                rows={1}
                disabled={busy}
                data-testid="chatbot-input"
                className="flex-1 resize-none border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 max-h-24"
              />
              <button
                onClick={() => send()}
                disabled={busy || !!streaming || !input.trim()}
                data-testid="chatbot-send"
                className="h-9 w-9 shrink-0 bg-zinc-950 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 flex items-center justify-center"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
            <div className="text-[9px] text-zinc-400 text-center mt-1.5">
              Réduire conserve la conversation · X efface l'historique · Cliquez à l'extérieur pour réduire
            </div>
          </div>
        </div>
        </>
      )}
    </>
  );
}
